import React, {useEffect, useMemo, useState} from "react";
import {
    clicksStatsOrderingList,
    conversionStatsColumnsList,
    params,
    sortDateOptions,
    statisticsStatusOptions
} from "../../../../../utils/vars/params";
import {useDispatch, useSelector} from "react-redux";
import {
    fetchConversionStats,
    resetTotalCount,
    statisticsSelector
} from "../../../../../redux/slices/statisticsSlice";
import {redirectorSelector} from "../../../../../redux/slices/redirectorSlice";
import {fetchUsersList, globalDataSelector, setAdaptiveMenu} from "../../../../../redux/slices/globalDataSlice";
import {applicationSelector} from "../../../../../redux/slices/applicationSlice";
import {managementSelector} from "../../../../../redux/slices/managementSlice";
import {Link, useSearchParams} from "react-router-dom";
import useGetSearchParams from "../../../../../hooks/useGetSearchParams";
import useSetSearchParams from "../../../../../hooks/useSetSearchParams";
import {useTranslation} from "react-i18next";
import {useSearchParamsOptions, useSelectOptions} from "../../../../../utils/helpers/useSelectHelpers";
import {is_tech_user} from "../../../../../storage/user";
import {useDebounce} from "../../../../../hooks/useDebounce";
import {fetchConversionStatsFilters} from "../../../../../api/helpers/fetchFilters";
import {getDefaultParams} from "../../../../../api/helpers/scripts";
import {INVALID_PAGE_ERROR} from "../../../../../utils/vars/staticVariables";
import {onSetPageSize} from "../../../../../utils/helpers/statisticsHelper";
import s from "./iosConversionStats.module.scss";
import {setTopUpBalanceActive} from "../../../../../redux/slices/financeSlice";
import SvgSelector from "../../../../../components/SvgSelector/SvgSelector";
import LanguageDropdown from "../../../../../components/LanguageDropdown/LanguageDropdown";
import Search from "../../../../../components/Search/Search";
import MultiSelect from "../../../../../components/Selects/MultiSelect/MultiSelect";
import {MuiDatePicker} from "../../../../../components/DatePickers/MuiDatePicker";
import {
    formatDateTime,
    formatIsoDateToReadable,
    formatTimestampToServerDate,
    stringToDayJs
} from "../../../../../utils/helpers/formatTime";
import FilterApplicationSelect from "../../../../../components/Selects/FilterApplicationSelect";
import GeoSelect from "../../../../../components/Selects/GeoSelect";
import {cleanSearchParams, hasNonEmptyParams} from "../../../../../utils/helpers/cleanFilters";
import EmptyList from "../../../../../components/EmptyList/EmptyList";
import ParamsSort from "../../../../../components/ParamsSort/ParamsSort";
import CustomSkeleton from "../../../../../components/UiKit/CustomSkeleton/CustomSkeleton";
import {PageSizeDropdown} from "../../../../../components/UiKit/PageSizeDropdown/PageSizeDropdown";
import Pagination from "../../../../../components/Pagination/Pagination";
import {
    application_name, conversion_date_time, conversion_type, created_at_after, created_at_before, email,
    geo,
    onSortDateHandler,
    page,
    search,
    sort,

    sub7
} from "../IOSClicksStats/IOSClicksStats";
import {MuiDateTimePicker} from "../../../../../components/DatePickers/MuiDateTimePicker";
import dayjs from "dayjs";
import {GlobalStyles} from "@mui/material";
import {capitalizeWord} from "../../../../../utils/helpers/capitalizeWord";
import Header from "../../../../../components/Partials/Header/Header";

const IOSConversionStats = () => {
    const [sortDate, setSortDate] = useState(sortDateOptions[0]);
    const [selectedGeos, setSelectedGeos] = useState([]);

    const dispatch = useDispatch();

    const {statisticsList, loading, totalCount} =
        useSelector(statisticsSelector);

    const {redirectorsList} = useSelector(redirectorSelector);
    const {
        geoList,
        isCollapsedSidebar,
        isAdaptiveMenuActive,
        pageSizeValue,
        usersList
    } = useSelector(globalDataSelector);
    const {appList} = useSelector(applicationSelector);
    const {standardClickPriceValue} = useSelector(managementSelector);

    const [searchParams, setSearchParams] = useSearchParams();
    const {
        search: urlSearchValue,
        sort: sortValue,
        page: pageValue,
        created_at_after: createdAtAfterParam,
        created_at_before: createdAtBeforeParam,
        conversion_date_time: conversion_dateTime,
        email: emailParam,
    } = useGetSearchParams();

    const updateSearchParams = useSetSearchParams(setSearchParams);

    const {t} = useTranslation();

    const [selectedSub7, selectedStatus, selectedApplications] =
        useSearchParamsOptions(searchParams, [
            sub7,
            conversion_type,
            application_name,
        ]);

    const selectedEmail = useMemo(() => {
        return emailParam ? { value: emailParam, label: emailParam } : null;
    }, [emailParam]);

    const sub7ListOptions = useSelectOptions(redirectorsList, "sub7", "sub7");

    const geoListOptions = useMemo(() => {
        return geoList.map((item) => ({
            label: item.name,
            value: item.code,
            icon: item.flag,
        }));
    }, [geoList]);

    const appListOptions = useMemo(() => {
        return appList.map((item) => ({
            label: item.name,
            value: item.name,
            is_alive: is_tech_user ? item.is_alive : true,
            name: "app_list",
            icon: item.icon,
        }));
    }, [appList]);

    const emailsListOptions = useSelectOptions(usersList, "email", "email");

    const [inputSearchValue, setInputSearchValue] = useDebounce((value) => {
        if (value) {
            updateSearchParams(search, value);
        } else {
            updateSearchParams(search, value, true);
        }
    }, 600);

    useEffect(() => {
        fetchConversionStatsFilters(dispatch);
    }, [dispatch]);

    useEffect(() => {
        if (!urlSearchValue) {
            setInputSearchValue("");
        } else {
            setInputSearchValue(urlSearchValue);
        }

        if (
            createdAtAfterParam ||
            createdAtBeforeParam ||
            sortDate.value === "custom_date"
        ) {
            setSortDate(sortDateOptions.find((item) => item.value === "custom_date"));
        } else {
            if (!sortValue) {
                setSortDate(sortDateOptions[0]);
            } else {
                setSortDate(sortDateOptions.find(({value}) => value === sortValue));
            }
        }
    }, [searchParams]);

    useEffect(() => {
        const newGeoListOptions = searchParams
            .get(geo)
            ?.split(",")
            .filter((value) => value !== "")
            .map((value) => {
                return geoListOptions.find((item) => item.value === value);
            });

        if (!newGeoListOptions || newGeoListOptions.length === 0) {
            setSelectedGeos([]);
        } else {
            setSelectedGeos(newGeoListOptions);
        }
    }, [searchParams, geoListOptions]);

    useEffect(() => {
        if (is_tech_user) {
            dispatch(fetchUsersList());
        }
    }, [dispatch]);

    useEffect(() => {
        const promise = dispatch(
            fetchConversionStats(
                getDefaultParams({
                    searchParams,
                    sort,
                    defaultPageSize: pageSizeValue,
                }),
            ),
        );

        promise.unwrap().catch((err) => {
            if (err?.message === INVALID_PAGE_ERROR) {
                updateSearchParams(page, 1);
            }
        });

        return () => {
            promise.abort();
        };
    }, [dispatch, searchParams, pageSizeValue]);

    useEffect(() => {
        return () => dispatch(resetTotalCount());
    }, [dispatch]);

    const cleanFilters = () => {
        cleanSearchParams(searchParams, setSearchParams)
        setSelectedGeos([]);
    };

    const handleSetPageSize = (size) => {
        onSetPageSize(dispatch, size);
    };

    const selectedDate = (() => {
        const foundOption = sortDateOptions.find(
            ({value}) => value === sortDate?.value,
        );

        if (foundOption) {
            return {...foundOption, label: t(foundOption.value)};
        }

        return null;
    })();

    const tableThead =
        <thead>
        <tr>
            {conversionStatsColumnsList.map(({ordering, name}) => (
                <th key={name}>
                    <div className={s.headerContent}>
                        {t(name)}
                        {
                            ordering && <ParamsSort
                                sortBy={ordering}
                                searchParams={searchParams}
                                setSearchParams={setSearchParams}
                            />
                        }
                    </div>
                </th>
            ))}
        </tr>
        </thead>

    return (
        <div
            className={`${s.clicksStatsWrapper} ${isCollapsedSidebar === "true" ? s.collapsedSidebar : ""}`}
        >
            <Header
                searchValue={inputSearchValue}
                setSearchValue={setInputSearchValue}
                isSearch
            />
            <div className={s.pageContent}>
                <div className={s.filtersWrapper}>
                    <div className={s.filters}>
                        <div className={s.selectDateWrapper}>
                            <div className={s.selectIconWrapper}>
                                <SvgSelector id="date-select-icon"/>
                            </div>
                            <MultiSelect
                                isClearable={false}
                                options={sortDateOptions.map((option) => {
                                    return {
                                        ...option,
                                        label: t(option.value),
                                    };
                                })}
                                setSelectedOptions={(value) =>
                                    onSortDateHandler(value, updateSearchParams, setSortDate)
                                }
                                value={selectedDate}
                                placeholder={t("date")}
                                isMulti={false}
                            />
                        </div>
                        {sortDate?.value === "custom_date" && (
                            <div className={s.datePickersWrapper}>
                                <div className={s.datePicker}>
                                    <MuiDatePicker
                                        label={t("date_from")}
                                        searchParamValue={created_at_after}
                                        updateSearchParams={updateSearchParams}
                                    />
                                </div>
                                <div className={s.datePicker}>
                                    <MuiDatePicker
                                        label={t("date_to")}
                                        searchParamValue={created_at_before}
                                        updateSearchParams={updateSearchParams}
                                    />
                                </div>
                            </div>
                        )}
                        <div className={s.datePicker}>
                            <GlobalStyles
                                styles={{
                                    '::-webkit-scrollbar': {
                                        width: '6px',
                                    },
                                    '::-webkit-scrollbar-thumb': {
                                        backgroundColor: '#FFFFFF33',
                                        borderRadius: '10px',
                                    },
                                    '::-webkit-scrollbar-track': {
                                        backgroundColor: '#FFFFFF1A',
                                    },
                                }}
                            />
                            <MuiDateTimePicker
                                label={t("conversion_date")}
                                value={conversion_dateTime ? dayjs(conversion_dateTime, "YYYY-MM-DD HH:mm") : null}
                                searchParamValue={conversion_date_time}
                                updateSearchParams={updateSearchParams}
                            />
                        </div>

                        <div className={s.selectWrapper}>
                            <div className={s.selectIconWrapper}>
                                <SvgSelector id="app-select-icon"/>
                            </div>
                            <FilterApplicationSelect
                                isClearable={true}
                                value={selectedApplications}
                                options={appListOptions}
                                onChange={(values) =>
                                    updateSearchParams(
                                        application_name,
                                        values.map((el) => el.value).join(","),
                                    )
                                }
                                placeholder={t("application_placeholder")}
                                isMulti={true}
                            />
                        </div>

                        <div className={s.selectWrapper}>
                            <div className={s.selectIconWrapper}>
                                <SvgSelector id="geo-select-icon"/>
                            </div>
                            <GeoSelect
                                isClearable={true}
                                value={selectedGeos}
                                options={geoListOptions}
                                onChange={(values) =>
                                    updateSearchParams(
                                        geo,
                                        values.map((el) => el.value).join(","),
                                    )
                                }
                                placeholder={t("geo_placeholder")}
                                isMulti={true}
                            />
                        </div>

                        <div className={s.selectWrapper}>
                            <div className={s.selectIconWrapper}>
                                <SvgSelector id="sub7-select-icon"/>
                            </div>
                            <MultiSelect
                                isClearable={true}
                                value={selectedSub7}
                                setSelectedOptions={(values) =>
                                    updateSearchParams(
                                        sub7,
                                        values.map((el) => el.value).join(","),
                                    )
                                }
                                options={sub7ListOptions}
                                placeholder="Sub7"
                                isMulti={true}
                            />
                        </div>
                        <div className={s.selectWrapper}>
                            <div className={s.selectIconWrapper}>
                                <SvgSelector id="status-select-icon"/>
                            </div>
                            <MultiSelect
                                isClearable={true}
                                value={selectedStatus}
                                options={statisticsStatusOptions}
                                setSelectedOptions={(values) =>
                                    updateSearchParams(
                                        conversion_type,
                                        values.map((el) => el.value).join(","),
                                    )
                                }
                                placeholder={t("status")}
                                isMulti={true}
                            />
                        </div>

                        {
                            is_tech_user && <div className={s.selectWrapper}>
                                <div className={s.selectIconWrapper}>
                                    <SvgSelector id="user-select-icon"/>
                                </div>
                                <MultiSelect
                                    isClearable={true}
                                    value={selectedEmail}
                                    options={emailsListOptions}
                                    setSelectedOptions={(option) => {
                                        if (option) {
                                            const {value} = option;
                                            updateSearchParams(email, value);
                                        } else {
                                            updateSearchParams(email, "", true);
                                        }
                                    }}
                                    placeholder={t("user")}
                                />
                            </div>
                        }

                        {hasNonEmptyParams(searchParams) && (
                            <div className={s.cleanBtnContainer}>
                                <button className={s.blueBorderBtn} onClick={cleanFilters}>
                                    {t("clear_button")}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                <div className={s.pageInnerContent}>
                    {totalCount === 0 && !loading ? (
                        <EmptyList/>
                    ) : loading ? (
                        <section className={s.body}>
                            <table>
                                {tableThead}
                            </table>
                            <div className={s.skeletonWrapper}>
                                <CustomSkeleton styles={s.skeletonTable}/>
                            </div>
                        </section>
                    ) : (
                        <section className={s.body}>
                            <table>
                                {tableThead}
                                <tbody>
                                {statisticsList?.map(
                                    (
                                        {
                                            geo,
                                            tds_clickid,
                                            app,
                                            sub7,
                                            created_at,
                                            conversion_type,
                                            conversion_date_time
                                        },
                                        index,
                                    ) => {

                                        const conversionTypeStatus = statisticsStatusOptions.find(({value}) => value === conversion_type)

                                        const {date, time} = formatDateTime(created_at);
                                        const {date: conversionDate, time: conversionTime} = formatIsoDateToReadable(conversion_date_time);


                                        const geoFlag = geoList.find(
                                            (geoItem) => geoItem.code === geo,
                                        )?.flag;
                                        const appIcon = appList.find(
                                            (appItem) => appItem.name === app?.name,
                                        )?.icon;

                                        return (
                                            <tr key={index}>
                                                <td>{tds_clickid}</td>
                                                <td className={s.appTd}>
                                                    <span className={s.value}>
                                                      <span
                                                          className={s.iconWrapper}
                                                          style={
                                                              appIcon
                                                                  ? {
                                                                      backgroundImage: `url(${appIcon})`,
                                                                  }
                                                                  : {
                                                                      background: "grey",
                                                                  }
                                                          }
                                                      ></span>
                                                      <span>{app?.name}</span>
                                                    </span>
                                                </td>
                                                <td className={s.geoTd}>
                                                    <span className={s.value}>
                                                      <span
                                                          className={s.flagWrapper}
                                                          style={
                                                              geoFlag
                                                                  ? {
                                                                      backgroundImage: `url(${geoFlag})`,
                                                                  }
                                                                  : {
                                                                      background: "grey",
                                                                  }
                                                          }
                                                      ></span>
                                                      <span>{geo}</span>
                                                    </span>
                                                </td>
                                                <td>{sub7}</td>

                                                <td>
                                                    <p>{date}</p>
                                                    <p style={{marginTop: 7}}>{time}</p>
                                                </td>
                                                <td>
                                                    <p>{conversionDate}</p>
                                                    <p style={{marginTop: 7}}>{conversionTime}</p>
                                                </td>
                                                <td>
                                                    {capitalizeWord(conversionTypeStatus?.label)}
                                                </td>
                                            </tr>
                                        );
                                    },
                                )}
                                </tbody>
                            </table>
                        </section>
                    )}
                </div>

                <div
                    className={`${s.paginationContainerStatistics} ${isCollapsedSidebar === "true" ? s.collapsed : ""}`}
                >
                    <div className={s.countPageSizeContainer}>
                        <div className={s.newRecordsCount}>
                            {t("total")}: {!loading && <span>{totalCount}</span>}
                        </div>
                        <div className={s.pageSizeDropdownContainer}>
                            <PageSizeDropdown
                                setSize={handleSetPageSize}
                                size={pageSizeValue}
                            />
                        </div>
                    </div>
                    {totalCount > pageSizeValue && (
                        <Pagination
                            currentPage={parseInt(pageValue) || 1}
                            setCurrentPage={(page) => {
                                updateSearchParams(params.page, page);
                            }}
                            total={totalCount}
                            limit={pageSizeValue}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default IOSConversionStats;